/*
  FRALORA — search window in the header (patched Minimog 5.3 component).
  Results come from Shopify's regular search, rendered by sections/fr-search-suggest.liquid
  (Shopify Predictive Search doesn't support Hebrew, the store's language, so it returned nothing).
  Suggestions show in-stock products only (options[unavailable_products]=hide); the full results
  page (Enter) still shows everything.
  Also:
    1. The spinner is always turned off (success, error, no results, timeout).
    2. No results / failed request → "results for ..." link to the full search page.
    3. Fast typing: older requests are aborted, so a slow old response can't overwrite a newer one.
    4. Safety timeout of 8s.
  Results are cached per query only when the request succeeded.
*/
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input]",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]",
    };

    this.container = document.querySelector("[data-search-popup]");

    this.domNodes = queryDomNodes(this.selectors, this.container);

    this.cachedResults = {};
    this.transitionDuration = 300;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.searchPopupSearults = this.querySelector(".m-search-popup__result");
    this.popularSearches = this.querySelector("[data-popular-searches]");
    this.searchCount = this.querySelector("[data-search-count]");
    this.abortController = null;
    this.requestTimer = null;

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;
    popularSearchItems.forEach((itm) => (itm.href = createSearchLink(itm.dataset.psQuery)));
  };

  setupEventListeners() {
    const { clear } = this.domNodes;
    const form = this.querySelector("form.m-search-form");
    form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    clear.addEventListener("click", this.onClearSearch.bind(this));
    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.cancelRequest();
      this.toggleSpinnerLoading(false);
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onClearSearch(event) {
    event.preventDefault();
    this.input.value = "";
    this.onChange();
  }

  cancelRequest() {
    if (this.abortController) {
      try { this.abortController.abort(); } catch (e) {}
      this.abortController = null;
    }
    clearTimeout(this.requestTimer);
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();

    if (this.cachedResults[queryKey]) {
      this.cancelRequest();
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    const routes = (window.MinimogSettings && window.MinimogSettings.routes) || {};
    const base = routes.search_url || "/search";
    const params = new URLSearchParams();
    params.set("q", searchTerm);
    params.set("type", "product");
    params.set("options[prefix]", "last");
    params.set("options[unavailable_products]", "hide");
    params.set("options[fields]", "title,vendor,product_type,variants.title");
    params.set("section_id", "fr-search-suggest");
    const searchURL = `${base}?${params.toString()}`;

    this.cancelRequest();
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    this.abortController = controller;
    this.requestTimer = setTimeout(() => {
      if (this.abortController === controller) {
        this.cancelRequest();
        this.renderSearchResults("");
      }
    }, 8000);

    fetch(searchURL, controller ? { signal: controller.signal } : undefined)
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((text) => {
        if (this.abortController !== controller) return;
        clearTimeout(this.requestTimer);
        this.abortController = null;
        const doc = new DOMParser().parseFromString(text, "text/html");
        const section = doc.querySelector("#shopify-section-fr-search-suggest") || doc.querySelector("[id^='shopify-section-']");
        const resultsMarkup = section ? section.innerHTML : "";
        if (section) this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error && error.name === "AbortError") return;
        if (this.abortController !== controller && this.abortController !== null) return;
        clearTimeout(this.requestTimer);
        this.abortController = null;
        this.renderSearchResults("");
      });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup || "";
    this.setAttribute("results", true);
    const searchItemsWrapper = this.querySelector("[data-search-items-wrapper]");
    const hasResults = !!(searchItemsWrapper && searchItemsWrapper.childElementCount > 0);
    this.renderSearchQueryAndMessage(hasResults);

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(results) {
    const { input, searchQuery, searchMessage, moreResultIcon } = this.domNodes;
    const query = input ? input.value : this.getQuery();

    if (searchQuery) searchQuery.textContent = query;

    this.predictiveSearchResults.classList.remove("m:hidden");
    this.searchPopupSearults.classList.remove("m:hidden");

    if (searchMessage) searchMessage.textContent = searchMessage.dataset.resultsTitle || "";
    if (moreResultIcon) moreResultIcon.classList.toggle("m:hidden", !!results);
  }

  toggleSpinnerLoading(show) {
    const { loading, submit } = this.domNodes;
    if (submit) submit.style.visibility = show ? "hidden" : "visible";
    if (loading) loading.style.visibility = show ? "visible" : "hidden";
  }

  toggleClearSearch(show) {
    const { clear } = this.domNodes;
    if (clear) clear.style.visibility = show ? "visible" : "hidden";
  }

  open() {
    this.searchPopupSearults.style.setProperty(
      "--search-result-max-height",
      `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`
    );
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;

    this.popularSearches && this.popularSearches.classList.add("m:hidden");
    this.searchCount && this.searchCount.classList.remove("m:hidden");
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
      this.toggleClearSearch(false);
      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");
    this.predictiveSearchResults.classList.add("m:hidden");
    this.searchPopupSearults.classList.add("m:hidden");
    this.popularSearches && this.popularSearches.classList.remove("m:hidden");
    this.searchCount && this.searchCount.classList.add("m:hidden");
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector("predictive-search").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }
}

customElements.define("predictive-search", PredictiveSearch);
